import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { inventoryApi } from '../api/inventory-api-instance';
import { AppliedFilters } from '../components/AppliedFilters';
import { ErrorState } from '../components/ErrorState';
import { PageControls } from '../components/PageControls';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { SearchableSelect, type SearchableSelectOption } from '../components/SearchableSelect';
import { SortableTableHeader } from '../components/SortableTableHeader';
import type { Part } from '../domain/inventory';
import {
  FULL_FLEET_ITEMS_QUERY_KEY,
  FULL_FLEET_PAGE_SIZE,
} from '../features/dashboard/aggregations';
import { operationallyVisibleItems } from '../features/inventory/operational-visibility';
import { humanizeIdentifier, sortByDisplayLabel } from '../lib/formatting';
import { toMap } from '../lib/collections';

const PARTS_PAGE_SIZE = 25;
const partSortKeys = [
  'id',
  'type',
  'build',
  'serialized',
  'version',
  'modified',
  'modifiedBy',
] as const;
type PartSortKey = (typeof partSortKeys)[number];

function isPartSortKey(value: string | null): value is PartSortKey {
  return partSortKeys.some((key) => key === value);
}

function formatDateTime(value: string | undefined): string {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Not recorded'
    : `${date.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

export function PartsPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const typeFilter = params.get('type') ?? '';
  const buildFilter = params.get('build') ?? '';
  const sortParam = params.get('sort');
  const sortKey = isPartSortKey(sortParam) ? sortParam : null;
  const sortDirection = params.get('dir') === 'desc' ? 'desc' : 'asc';
  const requestedPage = Math.max(1, Number(params.get('page') ?? 1) || 1);
  const parts = useQuery({ queryKey: ['parts'], queryFn: () => inventoryApi.listParts() });
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => inventoryApi.listSites() });
  const items = useQuery({
    queryKey: FULL_FLEET_ITEMS_QUERY_KEY,
    queryFn: () => inventoryApi.listItems({ pageSize: FULL_FLEET_PAGE_SIZE }),
  });

  const itemCounts = useMemo(() => {
    const counts = new Map<string, number>();
    const visibleItems = operationallyVisibleItems(items.data?.data ?? [], toMap(sites.data));
    for (const item of visibleItems) {
      counts.set(item.partId, (counts.get(item.partId) ?? 0) + 1);
    }
    return counts;
  }, [items.data, sites.data]);

  const typeOptions = useMemo<SearchableSelectOption[]>(() => {
    const types = new Set((parts.data ?? []).map((part) => part.type));
    return [
      { value: '', label: 'All part types' },
      ...sortByDisplayLabel([...types], humanizeIdentifier).map((type) => ({
        value: type,
        label: humanizeIdentifier(type),
      })),
    ];
  }, [parts.data]);

  const buildOptions = useMemo<SearchableSelectOption[]>(() => {
    const builds = new Set((parts.data ?? []).map((part) => part.build));
    return [
      { value: '', label: 'All build stages' },
      ...sortByDisplayLabel([...builds], humanizeIdentifier).map((build) => ({
        value: build,
        label: humanizeIdentifier(build),
      })),
    ];
  }, [parts.data]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matchingParts = (parts.data ?? []).filter(
      (part) =>
        `${part.id} ${part.type} ${part.build}`.toLowerCase().includes(needle) &&
        (!typeFilter || part.type === typeFilter) &&
        (!buildFilter || part.build === buildFilter),
    );

    return matchingParts.sort((left, right) => {
      if (!sortKey) {
        return `${left.type} ${left.build}`.localeCompare(`${right.type} ${right.build}`);
      }
      const comparison = compareParts(left, right, sortKey, itemCounts);
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [buildFilter, itemCounts, parts.data, query, sortDirection, sortKey, typeFilter]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PARTS_PAGE_SIZE));
  const page = Math.min(requestedPage, pageCount);
  const visibleParts = filtered.slice((page - 1) * PARTS_PAGE_SIZE, page * PARTS_PAGE_SIZE);

  function setPage(nextPage: number): void {
    const next = new URLSearchParams(params);
    if (nextPage <= 1) next.delete('page');
    else next.set('page', String(nextPage));
    setParams(next);
  }

  function setFilter(name: 'q' | 'type' | 'build', value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value);
    else next.delete(name);
    next.delete('page');
    setParams(next);
  }

  function updateSort(key: PartSortKey): void {
    const next = new URLSearchParams(params);
    if (sortKey === key) {
      if (sortDirection === 'asc') next.set('dir', 'desc');
      else {
        next.delete('sort');
        next.delete('dir');
      }
    } else {
      next.set('sort', key);
      next.set('dir', 'asc');
    }
    next.delete('page');
    setParams(next);
  }

  if (parts.isLoading || sites.isLoading || items.isLoading) {
    return <PageLoadingState label="Loading parts catalog…" />;
  }
  if (parts.isError || sites.isError || items.isError) {
    return (
      <ErrorState
        title="Parts catalog could not be loaded"
        rpcName="ListParts"
        onRetry={() => {
          void parts.refetch();
          void sites.refetch();
          void items.refetch();
        }}
      />
    );
  }

  const serializedCount = [...itemCounts.values()].reduce((sum, count) => sum + count, 0);
  const activeFilters = [
    query.trim()
      ? { key: 'q', label: `Search: ${query.trim()}`, onRemove: () => setFilter('q', '') }
      : null,
    typeFilter
      ? {
          key: 'type',
          label: `Part type: ${humanizeIdentifier(typeFilter)}`,
          onRemove: () => setFilter('type', ''),
        }
      : null,
    buildFilter
      ? {
          key: 'build',
          label: `Build stage: ${humanizeIdentifier(buildFilter)}`,
          onRemove: () => setFilter('build', ''),
        }
      : null,
    sortKey
      ? {
          key: 'sort',
          label: `Sort: ${partSortLabel(sortKey)} (${sortDirection})`,
          onRemove: () => {
            const next = new URLSearchParams(params);
            next.delete('sort');
            next.delete('dir');
            setParams(next);
          },
        }
      : null,
  ].filter(
    (filter): filter is { key: string; label: string; onRemove: () => void } => filter !== null,
  );

  return (
    <>
      <PageHeader
        title="Parts catalog"
        description="Part definitions and builds."
        actions={
          <Link className="button-link primary-link" to="/inventory">
            Browse serialized inventory
          </Link>
        }
      />

      <section className="compact-metrics" aria-label="Parts catalog summary">
        <div>
          <span>Definitions</span>
          <strong>{(parts.data ?? []).length.toLocaleString()}</strong>
        </div>
        <div>
          <span>Part types</span>
          <strong>{new Set((parts.data ?? []).map((part) => part.type)).size}</strong>
        </div>
        <div>
          <span>Build stages</span>
          <strong>{new Set((parts.data ?? []).map((part) => part.build)).size}</strong>
        </div>
        <div>
          <span>Serialized items</span>
          <strong>{serializedCount.toLocaleString()}</strong>
        </div>
      </section>

      <section className="reference-workspace" aria-label="Parts catalog results">
        <form
          className="parts-catalog-controls"
          role="search"
          aria-label="Filter parts catalog"
          onSubmit={(event) => event.preventDefault()}
        >
          <label className="parts-catalog-search">
            <span>Search</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setFilter('q', event.target.value)}
              placeholder="Part ID, type, or build stage"
              aria-label="Search parts"
            />
          </label>
          <label>
            <span>Part type</span>
            <SearchableSelect
              ariaLabel="Part type"
              value={typeFilter}
              onChange={(value) => setFilter('type', value)}
              options={typeOptions}
              placeholder="All part types"
            />
          </label>
          <label>
            <span>Build stage</span>
            <SearchableSelect
              ariaLabel="Build stage"
              value={buildFilter}
              onChange={(value) => setFilter('build', value)}
              options={buildOptions}
              placeholder="All build stages"
            />
          </label>
        </form>
        <AppliedFilters filters={activeFilters} />

        {filtered.length === 0 ? (
          <div className="empty-state">
            <strong>No part definitions match these filters.</strong>
            <button type="button" onClick={() => setParams(new URLSearchParams())}>
              Clear filters
            </button>
          </div>
        ) : (
          <div className="table-wrap reference-table">
            <table>
              <thead>
                <tr>
                  <SortableTableHeader
                    label="Part ID"
                    sortKey="id"
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={(key) => updateSort(key as PartSortKey)}
                  />
                  <SortableTableHeader
                    label="Part type"
                    sortKey="type"
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={(key) => updateSort(key as PartSortKey)}
                  />
                  <SortableTableHeader
                    label="Build stage"
                    sortKey="build"
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={(key) => updateSort(key as PartSortKey)}
                  />
                  <SortableTableHeader
                    label="Serialized items"
                    sortKey="serialized"
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={(key) => updateSort(key as PartSortKey)}
                  />
                  <SortableTableHeader
                    label="Version"
                    sortKey="version"
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={(key) => updateSort(key as PartSortKey)}
                  />
                  <SortableTableHeader
                    label="Last modified"
                    sortKey="modified"
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={(key) => updateSort(key as PartSortKey)}
                  />
                  <SortableTableHeader
                    label="Modified by"
                    sortKey="modifiedBy"
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={(key) => updateSort(key as PartSortKey)}
                  />
                </tr>
              </thead>
              <tbody>
                {visibleParts.map((part) => {
                  const count = itemCounts.get(part.id) ?? 0;
                  return (
                    <tr key={part.id}>
                      <th scope="row" className="mono">
                        {part.id}
                      </th>
                      <td>{humanizeIdentifier(part.type)}</td>
                      <td>{humanizeIdentifier(part.build)}</td>
                      <td>
                        <Link to={`/inventory?partId=${encodeURIComponent(part.id)}`}>
                          {count.toLocaleString()} items
                        </Link>
                      </td>
                      <td className="mono numeric-cell">{part.version}</td>
                      <td className="mono">{formatDateTime(part.lastModified)}</td>
                      <td>{part.lastModifiedBy ?? 'Not recorded'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {filtered.length > 0 ? (
          <PageControls
            page={page}
            pageSize={PARTS_PAGE_SIZE}
            total={filtered.length}
            onPageChange={setPage}
          />
        ) : null}
      </section>
    </>
  );
}

function compareParts(
  left: Part,
  right: Part,
  sortKey: PartSortKey,
  itemCounts: ReadonlyMap<string, number>,
): number {
  if (sortKey === 'serialized')
    return (itemCounts.get(left.id) ?? 0) - (itemCounts.get(right.id) ?? 0);
  if (sortKey === 'version') return left.version - right.version;

  const values = {
    id: left.id.localeCompare(right.id),
    type: humanizeIdentifier(left.type).localeCompare(humanizeIdentifier(right.type)),
    build: humanizeIdentifier(left.build).localeCompare(humanizeIdentifier(right.build)),
    modified: (left.lastModified ?? '').localeCompare(right.lastModified ?? ''),
    modifiedBy: (left.lastModifiedBy ?? '').localeCompare(right.lastModifiedBy ?? ''),
  };
  return values[sortKey];
}

function partSortLabel(key: PartSortKey): string {
  return {
    id: 'Part ID',
    type: 'Part type',
    build: 'Build stage',
    serialized: 'Serialized items',
    version: 'Version',
    modified: 'Last modified',
    modifiedBy: 'Modified by',
  }[key];
}
